import { ErrorMessage, useField } from 'formik';

import { CustomErrorMessage } from '@src/design-system/error-message';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import { InfiniteList } from '@src/design-system/molecules/infinite-list';
import { ChevronDownIcon } from 'lucide-react';
import { Label } from '@src/design-system/label';

import programApi from '@src/features/programs/services/program-api';
import { PROGRAM_STATUSES } from '@src/shared/types/program';
import type { TProgram } from '@src/shared/types/program';

export function ProgramSelect({
  name = 'program',
  selectPlaceholder = 'Select Program',
}: {
  name?: string;
  selectPlaceholder?: string;
}) {
  const [fieldProps, , helpers] = useField<TProgram | null>(name);
  const selected = fieldProps.value;

  return (
    <div>
      <Label>Program</Label>
      <Popover modal>
        <PopoverTrigger
          className={cn(
            inputVariants(),
            'mt-2 w-full flex justify-between items-center'
          )}
        >
          {selected?.name ? (
            <span className='truncate text-sm'>{selected.name}</span>
          ) : (
            <span className='text-sm text-gray-500'>{selectPlaceholder}</span>
          )}

          <ChevronDownIcon size={20} />
        </PopoverTrigger>
        <PopoverContent
          withPortal={false}
          style={{ width: 'var(--radix-popover-trigger-width)' }}
          align='start'
          className='p-2'
        >
          <InfiniteList
            fetchInfiniteData={programApi.useFetchInfinite}
            query={{ status: PROGRAM_STATUSES.active }}
            animateOnEveryRender
            props={{ listProps: { className: 'gap-0' } }}
            enableSearch
            searchParamName='program'
            renderItem={(p) => {
              const isSelected = selected?.id === p.id;

              return (
                <PopoverClose
                  onClick={() => helpers.setValue(p)}
                  className={cn(
                    'p-2 text-left hover:bg-primary-30 cursor-pointer text-sm inline-block w-full',
                    isSelected && 'bg-primary-100 hover:bg-primary-100'
                  )}
                >
                  {p.name}
                </PopoverClose>
              );
            }}
          />
        </PopoverContent>
      </Popover>

      <ErrorMessage
        name={fieldProps.name}
        component={CustomErrorMessage}
      />
    </div>
  );
}
